/**
 * NHL Edge Stats API Service
 *
 * Consomme les endpoints NHL Edge via le proxy Supabase `nhl-data`.
 * Fournit des statistiques avancées : vitesse de patinage, vitesse de tir,
 * distance parcourue, temps de zone et emplacement des tirs.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const NHL_DATA_FUNCTION_PATH = '/functions/v1/nhl-data';
const EDGE_API_BASE = 'https://api-web.nhle.com/v1/edge';
const CACHE_TTL_MS = 1000 * 60 * 10; // 10 minutes

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

interface CacheEntry<T> {
  expiresAt: number;
  data: T;
}

const edgeStatsCache = new Map<string, CacheEntry<unknown>>();

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NhlEdgeRankedMetric {
  rank?: number;
  total?: number;
  [key: string]: unknown;
}

export interface NhlEdgeSkaterDetail {
  topShotSpeed?: { heading?: Record<string, unknown>; playerSeason?: NhlEdgeRankedMetric & { maxSpeed?: number; gamesPlayed?: number; attempts?: number } };
  skatingSpeed?: { heading?: Record<string, unknown>; playerSeason?: NhlEdgeRankedMetric & { maxSpeed?: number; gamesPlayed?: number; burstsOver22?: number; burstsOver20?: number } };
  skatingSummary?: { heading?: Record<string, unknown>; playerSeason?: NhlEdgeRankedMetric & { totalDistance?: number; avgDistance?: number; gamesPlayed?: number } };
  zoneTime?: { heading?: Record<string, unknown>; playerSeason?: NhlEdgeRankedMetric & { offensiveZonePctg?: number; neutralZonePctg?: number; defensiveZonePctg?: number; offensiveZoneTime?: number; neutralZoneTime?: number; defensiveZoneTime?: number } };
  sogSummary?: { heading?: Record<string, unknown>; playerSeason?: NhlEdgeRankedMetric & { hdSog?: number; mdSog?: number; ldSog?: number; hdGoals?: number; mdGoals?: number; ldGoals?: number } };
  shotLocation?: { playerSeason?: NhlEdgeRankedMetric; details?: Array<{ category?: string; shots?: number; sog?: number; goals?: number; shootingPctg?: number }> };
}

export interface NhlEdgeGoalieDetail {
  gaa?: { playerSeason?: NhlEdgeRankedMetric & { value?: number } };
  gamesAbove900?: { playerSeason?: NhlEdgeRankedMetric & { value?: number; gamesPlayed?: number } };
  goalDifferentialPer60?: { playerSeason?: NhlEdgeRankedMetric & { value?: number } };
  goalSupport?: { playerSeason?: NhlEdgeRankedMetric & { value?: number } };
  pointPctg?: { playerSeason?: NhlEdgeRankedMetric & { value?: number } };
  shotLocation?: { playerSeason?: NhlEdgeRankedMetric; details?: Array<{ category?: string; shotsAgainst?: number; saves?: number; goalsAgainst?: number; savePctg?: number }> };
}

// ---------------------------------------------------------------------------
// Erreur personnalisée
// ---------------------------------------------------------------------------

export class NhlEdgeError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'NhlEdgeError';
  }
}

// ---------------------------------------------------------------------------
// Utilitaire de fetch via le proxy nhl-data
// ---------------------------------------------------------------------------

async function fetchFromNhlDataProxy<T>(nhlUrl: string): Promise<T> {
  const supabaseUrl = SUPABASE_URL;
  const supabaseAnonKey = SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new NhlEdgeError('Configuration Supabase manquante pour les stats NHL Edge');
  }

  const cacheKey = nhlUrl;
  const cached = edgeStatsCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data as T;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15_000);

  try {
    const response = await fetch(`${supabaseUrl}${NHL_DATA_FUNCTION_PATH}?endpoint=${encodeURIComponent(nhlUrl)}`, {
      signal: controller.signal,
      headers: {
        apikey: supabaseAnonKey,
        Authorization: 'Bearer ' + supabaseAnonKey,
      },
    });

    if (!response.ok) {
      throw new NhlEdgeError(`NHL Edge API indisponible (${response.status})`, response.status);
    }

    const data = (await response.json()) as T;

    if (data && typeof data === 'object') {
      edgeStatsCache.set(cacheKey, { data, expiresAt: Date.now() + CACHE_TTL_MS });
    }

    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NhlEdgeError('Délai dépassé pour les stats NHL Edge', 408);
    }
    if (error instanceof NhlEdgeError) throw error;
    throw new NhlEdgeError(error instanceof Error ? error.message : 'Erreur inconnue lors du chargement des stats Edge');
  } finally {
    clearTimeout(timeoutId);
  }
}

// ---------------------------------------------------------------------------
// Helpers d'extraction sécurisés
// ---------------------------------------------------------------------------

/** Calcule le percentile d'un rang (1 = meilleur). Retourne null si données manquantes. */
export function rankToPercentile(rank?: number, total?: number): number | null {
  if (rank == null || total == null || total <= 0) return null;
  return Math.round(((total - rank) / (total - 1)) * 100);
}

/** Retourne la couleur CSS selon le percentile (vert = élite, jaune = bon, gris = moyen) */
export function percentileToColorClass(percentile: number | null): string {
  if (percentile == null) return 'text-muted';
  if (percentile >= 90) return 'text-success';
  if (percentile >= 70) return 'text-info';
  if (percentile >= 50) return 'text-primary';
  if (percentile >= 30) return 'text-warning';
  return 'text-danger';
}

// Convertit un percentile (0..1) en rank/total (approx total=100)
function percentileToRankSimple(p?: number, total = 100): { rank?: number; total?: number } {
  if (p == null || typeof p !== 'number') return { rank: undefined, total: undefined };
  const rank = Math.max(1, Math.min(total, Math.round(total - p * (total - 1))));
  return { rank, total };
}

// ---------------------------------------------------------------------------
// Récupération et transformation des données skater
// ---------------------------------------------------------------------------

export async function fetchNhlEdgeSkaterDetail(playerId: number, season?: string, gameType: 2 | 3 = 2): Promise<NhlEdgeSkaterDetail> {
  if (!Number.isInteger(playerId) || playerId <= 0) throw new NhlEdgeError('Identifiant de joueur invalide');

  const path = season ? `${EDGE_API_BASE}/skater-detail/${playerId}/${season}/${gameType}` : `${EDGE_API_BASE}/skater-detail/${playerId}/now`;
  const raw = await fetchFromNhlDataProxy<any>(path);

  const percentileToRank = (p?: number, total = 100) => {
    if (p == null || typeof p !== 'number' || total <= 0) return { rank: undefined, total: undefined };
    // p is 0..1 (fraction). Inverse of rankToPercentile: rank = total - round(p*(total-1))
    const rank = Math.max(1, Math.min(total, Math.round(total - p * (total - 1))));
    return { rank, total };
  };

  const transformSkater = (rawData: any): NhlEdgeSkaterDetail => {
    if (!rawData || typeof rawData !== 'object') return {} as NhlEdgeSkaterDetail;

    const topShotSpeed = rawData.topShotSpeed
      ? (() => {
          const pct = rawData.topShotSpeed.percentile ?? rawData.topShotSpeed.playerPercentile;
          const rankInfo = percentileToRank(typeof pct === 'number' ? pct : undefined);
          return { playerSeason: { maxSpeed: rawData.topShotSpeed.imperial ?? rawData.topShotSpeed.metric, rank: rankInfo.rank, total: rankInfo.total } };
        })()
      : undefined;

    const skatingSpeed = rawData.skatingSpeed
      ? (() => {
          const pct = rawData.skatingSpeed.speedMax?.percentile ?? rawData.skatingSpeed.percentile;
          const rankInfo = percentileToRank(typeof pct === 'number' ? pct : undefined);
          return {
            playerSeason: {
              maxSpeed: rawData.skatingSpeed.speedMax?.imperial ?? rawData.skatingSpeed.speedMax?.metric,
              burstsOver22: rawData.skatingSpeed.burstsOver22?.value ?? rawData.skatingSpeed.burstsOver22 ?? undefined,
              burstsOver20: rawData.skatingSpeed.burstsOver20?.value ?? rawData.skatingSpeed.burstsOver20 ?? undefined,
              rank: rankInfo.rank,
              total: rankInfo.total,
            },
          };
        })()
      : undefined;

    const skatingSummary = rawData.totalDistanceSkated
      ? (() => {
          const pct = rawData.totalDistanceSkated.percentile;
          const rankInfo = percentileToRank(typeof pct === 'number' ? pct : undefined);
          return {
            playerSeason: {
              totalDistance: rawData.totalDistanceSkated.metric != null ? rawData.totalDistanceSkated.metric * 1000 : rawData.totalDistanceSkated.imperial,
              avgDistance: rawData.avgDistance ?? undefined,
              rank: rankInfo.rank,
              total: rankInfo.total,
            },
          };
        })()
      : undefined;

    let sogSummary: any = undefined;
    if (Array.isArray(rawData.sogSummary)) {
      const all = rawData.sogSummary.find((s: any) => s.locationCode === 'all');
      const high = rawData.sogSummary.find((s: any) => s.locationCode === 'high');
      const mid = rawData.sogSummary.find((s: any) => s.locationCode === 'mid');
      const long = rawData.sogSummary.find((s: any) => s.locationCode === 'long');
      const highPct = high?.shotsPercentile ?? high?.percentile;
      const midPct = mid?.shotsPercentile ?? mid?.percentile;
      const longPct = long?.shotsPercentile ?? long?.percentile;
      sogSummary = {
        playerSeason: {
          hdSog: high?.shots ?? undefined,
          mdSog: mid?.shots ?? undefined,
          ldSog: long?.shots ?? undefined,
          hdGoals: high?.goals ?? undefined,
          mdGoals: mid?.goals ?? undefined,
          ldGoals: long?.goals ?? undefined,
          rank: undefined,
          total: (highPct != null || midPct != null || longPct != null) ? 100 : undefined,
        },
      };
      // attach ranks per location in details (shotLocation will show rows)
      if (highPct != null) {
        const r = percentileToRank(highPct);
        sogSummary.playerSeason.hdRank = r.rank;
        sogSummary.playerSeason.hdTotal = r.total;
      }
      if (midPct != null) {
        const r = percentileToRank(midPct);
        sogSummary.playerSeason.mdRank = r.rank;
        sogSummary.playerSeason.mdTotal = r.total;
      }
      if (longPct != null) {
        const r = percentileToRank(longPct);
        sogSummary.playerSeason.ldRank = r.rank;
        sogSummary.playerSeason.ldTotal = r.total;
      }
    }

    const shotLocation = rawData.sogDetails
      ? { details: rawData.sogDetails.map((d: any) => ({ category: d.area, shots: d.shots, sog: d.shots, goals: d.goals, shootingPctg: d.shootingPctg })) }
      : undefined;

    const zoneTime = rawData.zoneTimeDetails
      ? (() => {
          const pctOff = rawData.zoneTimeDetails.offensiveZonePercentile ?? rawData.zoneTimeDetails.offensiveZonePctgPercentile ?? rawData.zoneTimeDetails.offensiveZonePercentile;
          const pctNeu = rawData.zoneTimeDetails.neutralZonePercentile ?? rawData.zoneTimeDetails.neutralZonePctgPercentile ?? rawData.zoneTimeDetails.neutralZonePercentile;
          const pctDef = rawData.zoneTimeDetails.defensiveZonePercentile ?? rawData.zoneTimeDetails.defensiveZonePctgPercentile ?? rawData.zoneTimeDetails.defensiveZonePercentile;
          const offRank = percentileToRank(typeof pctOff === 'number' ? pctOff : undefined);
          const neuRank = percentileToRank(typeof pctNeu === 'number' ? pctNeu : undefined);
          const defRank = percentileToRank(typeof pctDef === 'number' ? pctDef : undefined);
          return {
            playerSeason: {
              offensiveZonePctg: rawData.zoneTimeDetails.offensiveZonePctg,
              neutralZonePctg: rawData.zoneTimeDetails.neutralZonePctg,
              defensiveZonePctg: rawData.zoneTimeDetails.defensiveZonePctg,
              offensiveZoneTime: rawData.zoneTimeDetails.offensiveZoneTime,
              neutralZoneTime: rawData.zoneTimeDetails.neutralZoneTime,
              defensiveZoneTime: rawData.zoneTimeDetails.defensiveZoneTime,
              rank: offRank.rank ?? undefined,
              total: offRank.total ?? undefined,
              neutralRank: neuRank.rank ?? undefined,
              neutralTotal: neuRank.total ?? undefined,
              defensiveRank: defRank.rank ?? undefined,
              defensiveTotal: defRank.total ?? undefined,
            },
          };
        })()
      : undefined;

    return { topShotSpeed, skatingSpeed, skatingSummary, zoneTime, sogSummary, shotLocation } as NhlEdgeSkaterDetail;
  };

  // Détecte si la réponse contient des playerSeason utiles
  const isSkaterEmpty = (d?: NhlEdgeSkaterDetail) => {
    if (!d || typeof d !== 'object') return true;
    const ps = [d.skatingSpeed?.playerSeason, d.topShotSpeed?.playerSeason, d.skatingSummary?.playerSeason, d.zoneTime?.playerSeason, d.sogSummary?.playerSeason, d.shotLocation?.details];
    return ps.every((x) => x == null || (Array.isArray(x) && x.length === 0));
  };

  const data = transformSkater(raw);
  const hasData = !isSkaterEmpty(data);

  if (!hasData && !season) {
    const fallbackSeason = '20252026';
    try {
      const fallbackRaw = await fetchFromNhlDataProxy<any>(`${EDGE_API_BASE}/skater-detail/${playerId}/${fallbackSeason}/${gameType}`);
      const fallbackData = transformSkater(fallbackRaw);
      if (!isSkaterEmpty(fallbackData)) return fallbackData;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug('NHL Edge skater fallback failed', err);
    }
  }

  return data;
}

// ---------------------------------------------------------------------------
// Récupération goalie
// ---------------------------------------------------------------------------

export async function fetchNhlEdgeGoalieDetail(playerId: number, season?: string, gameType: 2 | 3 = 2): Promise<NhlEdgeGoalieDetail> {
  if (!Number.isInteger(playerId) || playerId <= 0) throw new NhlEdgeError('Identifiant de joueur invalide');

  const path = season ? `${EDGE_API_BASE}/goalie-detail/${playerId}/${season}/${gameType}` : `${EDGE_API_BASE}/goalie-detail/${playerId}/now`;
  const raw = await fetchFromNhlDataProxy<any>(path);

  const transformGoalie = (rawData: any): NhlEdgeGoalieDetail => {
    if (!rawData || typeof rawData !== 'object') return {} as NhlEdgeGoalieDetail;

    // Certaines réponses sont enveloppées sous `stats` (ex: API directe NHL Edge).
    // Normaliser la source pour supporter les deux formes.
    const src = rawData.stats ?? rawData;

    // Note: use src (normalised) for metric extraction; keep rawData for shot details variants
    const _gaa = src.goalsAgainstAvg ?? src.gaa ?? undefined;
    const gaa = _gaa
      ? (() => {
          // L'API peut fournir un percentile où les valeurs élevées signifient "pire" pour GAA.
          // Inverser le percentile pour que pctForRank suive la convention "plus grand = meilleur".
          const rawPct = _gaa.percentile ?? _gaa.playerPercentile ?? undefined;
          const pctForRank = typeof rawPct === 'number' ? 1 - rawPct : undefined;
          const r = percentileToRankSimple(pctForRank);
          return { playerSeason: { value: _gaa.value ?? _gaa, rank: r.rank, total: r.total } };
        })()
      : undefined;

    const _gamesAbove900 = src.gamesAbove900 ?? src.gamesAbove900 ?? undefined;
    const gamesAbove900 = _gamesAbove900
      ? (() => {
          const pct = _gamesAbove900.percentile ?? undefined;
          const r = percentileToRankSimple(typeof pct === 'number' ? pct : undefined);
          return { playerSeason: { value: _gamesAbove900.value ?? _gamesAbove900, rank: r.rank, total: r.total } };
        })()
      : undefined;

    const _goalDifferentialPer60 = src.goalDifferentialPer60 ?? src.goalDifferentialPer60 ?? undefined;
    const goalDifferentialPer60 = _goalDifferentialPer60
      ? (() => {
          const pct = _goalDifferentialPer60.percentile ?? undefined;
          const r = percentileToRankSimple(typeof pct === 'number' ? pct : undefined);
          return { playerSeason: { value: _goalDifferentialPer60.value ?? _goalDifferentialPer60, rank: r.rank, total: r.total } };
        })()
      : undefined;

    const _goalSupport = src.goalSupportAvg ?? src.goalSupport ?? undefined;
    const goalSupport = _goalSupport
      ? (() => {
          const pct = _goalSupport.percentile ?? undefined;
          const r = percentileToRankSimple(typeof pct === 'number' ? pct : undefined);
          return { playerSeason: { value: _goalSupport.value ?? _goalSupport, rank: r.rank, total: r.total } };
        })()
      : undefined;

    const _pointPctg = src.pointPctg ?? src.pointPctg ?? undefined;
    const pointPctg = _pointPctg
      ? (() => {
          const pct = _pointPctg.percentile ?? undefined;
          const r = percentileToRankSimple(typeof pct === 'number' ? pct : undefined);
          return { playerSeason: { value: _pointPctg.value ?? _pointPctg, rank: r.rank, total: r.total } };
        })()
      : undefined;

    const shotDetailsSource = rawData.shotLocation?.details ?? rawData.sogDetails ?? rawData.shotLocationDetails ?? rawData.shotLocationSummary ?? undefined;
    const shotLocation = shotDetailsSource ? { details: (shotDetailsSource ?? []).map((d: any) => ({ category: d.area ?? d.category ?? d.locationCode, shotsAgainst: d.shotsAgainst ?? d.shots ?? d.saves ?? undefined, saves: d.saves ?? undefined, goalsAgainst: d.goalsAgainst ?? d.goals ?? d.goalsAgainst ?? undefined, savePctg: d.savePctg ?? d.savePctg ?? d.savePctg })) } : undefined;

    return { gaa, gamesAbove900, goalDifferentialPer60, goalSupport, pointPctg, shotLocation } as NhlEdgeGoalieDetail;
  };

  const isGoalieEmpty = (d?: NhlEdgeGoalieDetail) => {
    if (!d || typeof d !== 'object') return true;
    const ps = [d.gaa?.playerSeason, d.gamesAbove900?.playerSeason, d.goalDifferentialPer60?.playerSeason, d.goalSupport?.playerSeason, d.pointPctg?.playerSeason, d.shotLocation?.details];
    return ps.every((x) => x == null || (Array.isArray(x) && x.length === 0));
  };

  const data = transformGoalie(raw);
  if (!isGoalieEmpty(data)) return data;

  if (!season) {
    const fallbackSeason = '20252026';
    try {
      const fallbackRaw = await fetchFromNhlDataProxy<any>(`${EDGE_API_BASE}/goalie-detail/${playerId}/${fallbackSeason}/${gameType}`);
      const fallbackData = transformGoalie(fallbackRaw);
      if (!isGoalieEmpty(fallbackData)) return fallbackData;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug('NHL Edge goalie fallback failed', err);
    }
  }

  return data;
}

// ---------------------------------------------------------------------------
// Cache clear
// ---------------------------------------------------------------------------

export function clearNhlEdgeCache(playerId?: number): void {
  if (playerId == null) {
    edgeStatsCache.clear();
    return;
  }
  for (const key of edgeStatsCache.keys()) {
    if (key.includes(`/${playerId}/`)) edgeStatsCache.delete(key);
  }
}
